import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { TOKEN_KEY } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { P } from "@/lib/permissions";
import { sharedGet, visibleInterval } from "@/lib/poll";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

export function AppProvider({ children }) {
  // Trading-only feeds are not polled for users without trading access (they would only 403).
  // This provider is mounted per user (App.js keys it by user.id), so nothing below can outlive a
  // sign-out or leak into another account in the same tab. The only persisted preference is keyed
  // by user id as well.
  const { user, can } = useAuth();
  const canSim = can(P.SIMULATOR);
  const envKey = `gz_env:${user?.id || "anon"}`;
  const [broker, setBroker] = useState(null);            // SIMULATION mock broker (ADMIN only)
  const [zerodha, setZerodha] = useState(null);          // real Zerodha session state
  const [systemStatus, setSystemStatus] = useState(null);
  const [config, setConfig] = useState(null);
  const [environment, setEnvironmentState] = useState(() => {
    const saved = localStorage.getItem(envKey);
    return saved === "SIMULATION" && canSim ? "SIMULATION" : "PAPER";
  });
  const [notifications, setNotifications] = useState([]);
  const [lastNotifId, setLastNotifId] = useState(null);

  const setEnvironment = useCallback((env) => {
    const next = env === "SIMULATION" && canSim ? "SIMULATION" : "PAPER";
    localStorage.setItem(envKey, next);
    setEnvironmentState(next);
  }, [envKey, canSim]);

  const refreshBroker = useCallback(async () => {
    try { const { data } = await api.get("/broker/session"); setBroker(data); } catch (e) {}
  }, []);

  const refreshZerodha = useCallback(async () => {
    try { const { data } = await sharedGet("/system-status"); setSystemStatus(data); setZerodha(data.session_detail); } catch (e) {}
  }, []);

  const refreshNotifications = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifications(data);
      if (data.length && lastNotifId && data[0].id !== lastNotifId) {
        const fresh = [];
        for (const n of data) { if (n.id === lastNotifId) break; fresh.push(n); }
        fresh.reverse().forEach((n) => {
          const fn = n.severity === "critical" || n.severity === "warning" ? toast.warning
            : n.severity === "success" ? toast.success : toast.info;
          fn(n.message);
        });
      }
      if (data.length) setLastNotifId(data[0].id);
    } catch (e) {}
  }, [lastNotifId]);

  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) return;
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
    // The simulator feeds (mock broker, simulator notifications) are ADMIN-only on the server.
    const tick = () => { refreshZerodha(); if (canSim) { refreshBroker(); refreshNotifications(); } };
    tick();
    return visibleInterval(tick, 5000);
  }, [refreshBroker, refreshZerodha, refreshNotifications, canSim]);

  const unread = notifications.filter((n) => !n.read).length;

  return (
    <AppContext.Provider value={{ broker, refreshBroker, zerodha, systemStatus, refreshZerodha,
      config, environment, setEnvironment, notifications, refreshNotifications, unread, canSim }}>
      {children}
    </AppContext.Provider>
  );
}
